import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { Users, Plus, Upload, Search, Pencil, Trash2, X, Loader2, CheckCircle2, Ticket, Clock, MapPin, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "@/lib/toastBanner";
import DeleteConfirmDialog from "@/components/DeleteConfirmDialog";
import AddAttendeesFromCohortModal from "@/components/AddAttendeesFromCohortModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Minimal CSV parser — handles quoted fields and commas inside quotes.
function parseCSV(text) {
  const rows = [];
  let i = 0;
  let cur = [""];
  let inQuotes = false;
  while (i < text.length) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"' && text[i + 1] === '"') { cur[cur.length - 1] += '"'; i += 2; continue; }
      if (ch === '"') { inQuotes = false; i++; continue; }
      cur[cur.length - 1] += ch; i++; continue;
    }
    if (ch === '"') { inQuotes = true; i++; continue; }
    if (ch === ",") { cur.push(""); i++; continue; }
    if (ch === "\r") { i++; continue; }
    if (ch === "\n") { rows.push(cur); cur = [""]; i++; continue; }
    cur[cur.length - 1] += ch; i++;
  }
  if (cur.length > 1 || (cur[0] || "").length > 0) rows.push(cur);
  return rows;
}

function rowsToAttendeeRecords(matrix) {
  if (!matrix.length) return [];
  const header = matrix[0].map((h) => (h || "").trim().toLowerCase());
  const idx = {
    first_name: header.indexOf("first_name") !== -1 ? header.indexOf("first_name") : header.indexOf("first name"),
    last_name: header.indexOf("last_name") !== -1 ? header.indexOf("last_name") : header.indexOf("last name"),
    email: header.indexOf("email"),
  };
  if (idx.first_name === -1 && idx.last_name === -1) return [];
  return matrix.slice(1)
    .map((r) => ({
      first_name: (r[idx.first_name] || "").trim(),
      last_name: (r[idx.last_name] || "").trim(),
      email: idx.email !== -1 ? (r[idx.email] || "").trim() : "",
    }))
    .filter((r) => r.first_name || r.last_name);
}

export default function EventAttendeesCard({ eventId, passports }) {
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  // Add modal
  const [addOpen, setAddOpen] = useState(false);
  const [addFirst, setAddFirst] = useState("");
  const [addLast, setAddLast] = useState("");
  const [addEmail, setAddEmail] = useState("");
  const [addPassportId, setAddPassportId] = useState("");
  const [adding, setAdding] = useState(false);

  // Edit modal
  const [editTarget, setEditTarget] = useState(null);
  const [editFirst, setEditFirst] = useState("");
  const [editLast, setEditLast] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [saving, setSaving] = useState(false);

  // Import modal
  const [importOpen, setImportOpen] = useState(false);
  const [fromCohortOpen, setFromCohortOpen] = useState(false);
  const [importPreview, setImportPreview] = useState([]);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Inline passport assignment in-flight tracker
  const [savingPassportFor, setSavingPassportFor] = useState(null);

  // Bulk-selection state
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkPassportId, setBulkPassportId] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  // Per-row mobile expand state (controls passport + actions visibility on
  // narrow screens; desktop always renders inline).
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Cache of resolved itineraries keyed by passport_id. Populated lazily as
  // soon as we see at least one attendee assigned to a given passport.
  const [itineraryCache, setItineraryCache] = useState({});

  const load = useCallback(async (opts = {}) => {
    if (!opts.silent) setLoading(true);
    try {
      const { data } = await axios.get(`${API}/events/${eventId}/attendees`, { withCredentials: true });
      setAttendees(Array.isArray(data) ? data : []);
    } catch {
      if (!opts.silent) toast.error("Failed to load attendees");
    } finally { if (!opts.silent) setLoading(false); }
  }, [eventId]);

  // Whenever the attendee list changes, fetch any new passport itineraries.
  useEffect(() => {
    const wanted = new Set();
    for (const a of attendees) {
      if (a.passport_id) wanted.add(a.passport_id);
    }
    const missing = Array.from(wanted).filter((pid) => !(pid in itineraryCache));
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      const results = await Promise.all(missing.map(async (pid) => {
        try {
          const { data } = await axios.get(
            `${API}/events/${eventId}/passports/${pid}/itinerary`,
            { withCredentials: true },
          );
          return [pid, Array.isArray(data?.itinerary) ? data.itinerary : []];
        } catch {
          return [pid, []];
        }
      }));
      if (cancelled) return;
      setItineraryCache((prev) => {
        const next = { ...prev };
        for (const [pid, it] of results) next[pid] = it;
        return next;
      });
    })();
    return () => { cancelled = true; };
  }, [attendees, eventId, itineraryCache]);

  useEffect(() => { load(); }, [load]);

  // Live-refresh so check-ins performed from the Check-In modal or the
  // public mobile flow are reflected here in real time (badge + per-passport
  // tally update without the admin needing to reload the page).
  useEffect(() => {
    const handle = setInterval(() => { load({ silent: true }); }, 5000);
    const onFocus = () => { load({ silent: true }); };
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(handle);
      window.removeEventListener("focus", onFocus);
    };
  }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return attendees;
    return attendees.filter((a) =>
      `${a.first_name} ${a.last_name} ${a.email}`.toLowerCase().includes(q)
    );
  }, [attendees, query]);

  const checkedInCount = useMemo(() => attendees.filter((a) => a.checked_in_at).length, [attendees]);

  // Per-passport check-in tally (informational, derived from the full
  // attendee list — not affected by the search filter).
  const passportTally = useMemo(() => {
    const counts = new Map(); // id -> {total, checkedIn}
    for (const a of attendees) {
      const key = a.passport_id || "_none";
      const prev = counts.get(key) || { total: 0, checkedIn: 0 };
      counts.set(key, {
        total: prev.total + 1,
        checkedIn: prev.checkedIn + (a.checked_in_at ? 1 : 0),
      });
    }
    const ordered = (passports || [])
      .map((p) => ({ id: p.id, name: p.name, ...(counts.get(p.id) || { total: 0, checkedIn: 0 }) }))
      .filter((b) => b.total > 0);
    const noPassport = counts.get("_none");
    return noPassport && noPassport.total > 0
      ? [...ordered, { id: "_none", name: "No Passport", ...noPassport }]
      : ordered;
  }, [attendees, passports]);

  // Bulk selection helpers — selection is scoped to currently-filtered rows
  const filteredIds = useMemo(() => filtered.map((a) => a.id), [filtered]);
  const allFilteredSelected = filteredIds.length > 0 && filteredIds.every((id) => selectedIds.has(id));
  const someFilteredSelected = !allFilteredSelected && filteredIds.some((id) => selectedIds.has(id));
  const selectedCount = useMemo(
    () => filteredIds.filter((id) => selectedIds.has(id)).length,
    [filteredIds, selectedIds],
  );

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAllFiltered = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) filteredIds.forEach((id) => next.delete(id));
      else filteredIds.forEach((id) => next.add(id));
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const applyBulkPassport = async () => {
    const ids = Array.from(selectedIds).filter((id) => filteredIds.includes(id));
    if (ids.length === 0) { toast.error("Select at least one attendee"); return; }
    setBulkBusy(true);
    try {
      const { data } = await axios.put(
        `${API}/events/${eventId}/attendees/passport-bulk`,
        { attendee_ids: ids, passport_id: bulkPassportId || null },
        { withCredentials: true },
      );
      toast.success(
        bulkPassportId
          ? `Assigned passport to ${data?.updated || ids.length} attendee${(data?.updated || ids.length) === 1 ? "" : "s"}`
          : `Cleared passport on ${data?.updated || ids.length} attendee${(data?.updated || ids.length) === 1 ? "" : "s"}`,
      );
      clearSelection();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to bulk assign");
    } finally { setBulkBusy(false); }
  };

  // ─── Add ─────────────────────────────────────────────────────────
  const openAdd = () => {
    setAddFirst(""); setAddLast(""); setAddEmail(""); setAddPassportId("");
    setAddOpen(true);
  };
  const submitAdd = async () => {
    const fn = addFirst.trim(); const ln = addLast.trim();
    if (!fn && !ln) { toast.error("Enter at least a first or last name"); return; }
    setAdding(true);
    try {
      const { data: created } = await axios.post(`${API}/events/${eventId}/attendees`, {
        first_name: fn, last_name: ln, email: addEmail.trim(),
      }, { withCredentials: true });
      // Assign the passport in a follow-up call if one was selected
      if (addPassportId && created?.id) {
        try {
          await axios.put(
            `${API}/events/${eventId}/attendees/${created.id}/passport`,
            { passport_id: addPassportId },
            { withCredentials: true },
          );
        } catch {
          toast.error("Attendee added, but passport could not be assigned");
        }
      }
      toast.success("Attendee added");
      setAddOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add attendee");
    } finally { setAdding(false); }
  };

  // ─── Edit ─────────────────────────────────────────────────────────
  const openEdit = (a) => {
    setEditTarget(a);
    setEditFirst(a.first_name || "");
    setEditLast(a.last_name || "");
    setEditEmail(a.email || "");
  };
  const submitEdit = async () => {
    if (!editTarget) return;
    const fn = editFirst.trim(); const ln = editLast.trim();
    if (!fn && !ln) { toast.error("Enter at least a first or last name"); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/events/${eventId}/attendees/${editTarget.id}`, {
        first_name: fn, last_name: ln, email: editEmail.trim(),
      }, { withCredentials: true });
      toast.success("Attendee updated");
      setEditTarget(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update");
    } finally { setSaving(false); }
  };

  // ─── Delete ──────────────────────────────────────────────────────
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/events/${eventId}/attendees/${deleteTarget.id}`, { withCredentials: true });
      toast.success("Attendee removed");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to remove");
    }
  };

  // ─── Import ──────────────────────────────────────────────────────
  const openImport = () => { setImportPreview([]); setImportOpen(true); };
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const text = await f.text();
      const matrix = parseCSV(text);
      const records = rowsToAttendeeRecords(matrix);
      if (!records.length) {
        toast.error("Could not find first_name / last_name columns in your CSV");
        e.target.value = "";
        return;
      }
      setImportPreview(records);
    } catch {
      toast.error("Failed to read file");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  const submitImport = async () => {
    if (!importPreview.length) return;
    setImporting(true);
    try {
      const { data } = await axios.post(
        `${API}/events/${eventId}/attendees/import`,
        { rows: importPreview },
        { withCredentials: true }
      );
      toast.success(`Imported ${data.imported}${data.skipped ? ` · ${data.skipped} skipped` : ""}`);
      setImportOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to import");
    } finally { setImporting(false); }
  };

  // ─── Passport assignment ─────────────────────────────────────────
  const setPassport = async (attendee, passportId) => {
    setSavingPassportFor(attendee.id);
    try {
      await axios.put(
        `${API}/events/${eventId}/attendees/${attendee.id}/passport`,
        { passport_id: passportId || null },
        { withCredentials: true }
      );
      // Refresh so passport_name (and other derived fields) stay in sync —
      // and the inline itinerary container picks up the new assignment.
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update passport");
    } finally { setSavingPassportFor(null); }
  };

  return (
    <div className="bg-[#11141A] border border-white/10 rounded-xl p-4 sm:p-5 overflow-hidden" data-testid="event-attendees-card">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h2 className="font-heading text-lg font-semibold text-white inline-flex items-center gap-2">
            <Users className="h-4 w-4 text-[#6E7787]" /> Attendees
          </h2>
          <p className="text-xs font-body text-[#6E7787] mt-0.5">
            Standalone per-event guest list — separate from cohorts. Add directly or import a CSV. Assign a passport when ready.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={() => setFromCohortOpen(true)}
            className="gap-2 border-white/20 text-white bg-transparent hover:bg-white/5"
            data-testid="attendees-from-cohort-btn"
          >
            <Users className="h-4 w-4" /> Add Attendees from Cohort
          </Button>
          <Button
            variant="outline"
            onClick={openImport}
            className="gap-2 border-white/20 text-white bg-transparent hover:bg-white/5"
            data-testid="attendees-import-btn"
          >
            <Upload className="h-4 w-4" /> Import CSV
          </Button>
          <Button
            onClick={openAdd}
            className="gap-2"
            style={{ background: "var(--primary-color)", color: "var(--admin-button-fg, #fff)" }}
            data-testid="attendees-add-btn"
          >
            <Plus className="h-4 w-4" /> Add Attendee
          </Button>
        </div>
      </div>

      {/* Stats strip */}
      {attendees.length > 0 ? (
        <div className="flex items-center gap-4 mb-3 text-xs font-body text-[#A1A9B8] flex-wrap" data-testid="attendees-stats">
          <span><span className="text-white font-semibold">{attendees.length}</span> total</span>
          <span><span className="text-emerald-400 font-semibold">{checkedInCount}</span> checked in</span>
        </div>
      ) : null}

      {/* Search */}
      {attendees.length > 0 ? (
        <div className="relative mb-3">
          <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#6E7787]" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or email"
            className="bg-[#0A0C10] border-white/10 text-white placeholder:text-[#6E7787] pl-9 font-body text-sm"
            data-testid="attendees-search"
          />
        </div>
      ) : null}

      {/* Per-passport check-in tally (informational) */}
      {passportTally.length > 0 ? (
        <div
          className="flex flex-wrap gap-2 mb-3 p-3 rounded-lg bg-white/[0.02] border border-white/10"
          data-testid="attendees-passport-tally"
        >
          {passportTally.map((b) => {
            const pct = b.total > 0 ? Math.round((b.checkedIn / b.total) * 100) : 0;
            const noPassport = b.id === "_none";
            return (
              <div
                key={b.id}
                className="flex-1 min-w-[160px] rounded-md px-3 py-2 bg-[#0A0C10] border border-white/10"
                data-testid={`tally-${b.id}`}
              >
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <span className={`text-xs font-body font-semibold truncate ${noPassport ? "text-[#6E7787]" : "text-white"}`}>
                    {noPassport ? null : <Ticket className="inline h-3 w-3 mr-1 -mt-0.5 opacity-70" />}
                    {b.name}
                  </span>
                  <span className="text-xs font-body tabular-nums text-[#A1A9B8] shrink-0">
                    {b.checkedIn} / {b.total}
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${pct}%`,
                      background: pct === 100 ? "#10b981" : "var(--primary-color)",
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      ) : null}

      {/* Bulk-select toolbar — appears once anything is selected */}
      {attendees.length > 0 ? (
        <div className="flex items-center gap-3 mb-3 flex-wrap" data-testid="attendees-bulk-toolbar">
          <label className="inline-flex items-center gap-2 cursor-pointer select-none">
            <Checkbox
              checked={allFilteredSelected ? true : (someFilteredSelected ? "indeterminate" : false)}
              onCheckedChange={toggleAllFiltered}
              className="h-4 w-4 border-2 border-black bg-white data-[state=checked]:bg-[var(--primary-color)] data-[state=checked]:border-black"
              data-testid="attendees-select-all"
            />
            <span className="text-xs font-body text-[#A1A9B8]">
              {selectedCount > 0 ? `${selectedCount} selected` : "Select all"}
            </span>
          </label>
          {selectedCount > 0 ? (
            <>
              <div className="flex items-center gap-2">
                <span className="text-xs font-body text-[#6E7787]">Passport:</span>
                <select
                  value={bulkPassportId}
                  onChange={(e) => setBulkPassportId(e.target.value)}
                  disabled={bulkBusy || !passports?.length}
                  className="h-8 rounded bg-[#0A0C10] border border-white/10 text-white text-xs font-body px-2 disabled:opacity-50 focus:outline-none focus:border-white/30"
                  data-testid="attendees-bulk-passport-select"
                >
                  <option value="">— No passport —</option>
                  {(passports || []).map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <Button
                size="sm"
                onClick={applyBulkPassport}
                disabled={bulkBusy}
                style={{ background: "var(--primary-color)", color: "var(--admin-button-fg, #fff)" }}
                className="gap-1.5"
                data-testid="attendees-bulk-apply"
              >
                {bulkBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Ticket className="h-3.5 w-3.5" />}
                {bulkPassportId ? "Assign" : "Clear Passport"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={clearSelection}
                disabled={bulkBusy}
                className="border-white/20 text-white bg-transparent hover:bg-white/5"
                data-testid="attendees-bulk-clear"
              >
                Clear
              </Button>
            </>
          ) : null}
        </div>
      ) : null}

      {/* List */}
      {loading ? (
        <div className="py-8 flex items-center justify-center text-[#6E7787]">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : attendees.length === 0 ? (
        <div className="rounded-lg border border-dashed border-white/10 px-6 py-8 text-center" data-testid="attendees-empty">
          <Users className="h-8 w-8 mx-auto text-[#6E7787]" />
          <p className="text-sm font-body text-[#6E7787] mt-2">
            No attendees yet. Click <span className="text-white">Add Attendee</span> or <span className="text-white">Import CSV</span> to build the list.
          </p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed border-white/10 px-6 py-6 text-center">
          <p className="text-sm font-body text-[#6E7787]">No attendees match &quot;{query}&quot;.</p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="attendees-list">
          {filtered.map((a) => {
            const isExpanded = expandedIds.has(a.id);
            return (
            <li
              key={a.id}
              className={`rounded-lg border px-3 sm:px-4 py-3 transition-colors overflow-hidden ${selectedIds.has(a.id) ? "border-[var(--primary-color)] bg-white/[0.02]" : "border-white/10 hover:border-white/20"}`}
              data-testid={`attendee-row-${a.id}`}
            >
              {/* Top row — always visible. Mobile: name + checked-in + chevron.
                  Desktop: name + checked-in + itinerary + actions on the right. */}
              <div className="flex items-start sm:items-center justify-between gap-2 sm:gap-3">
                <div className="min-w-0 flex-1 flex items-start sm:items-center gap-3">
                  <Checkbox
                    checked={selectedIds.has(a.id)}
                    onCheckedChange={() => toggleOne(a.id)}
                    className="h-4 w-4 mt-1 sm:mt-0 border-2 border-black bg-white data-[state=checked]:bg-[var(--primary-color)] data-[state=checked]:border-black shrink-0"
                    data-testid={`attendee-select-${a.id}`}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-body font-semibold text-white truncate">
                        {(a.first_name || "") + (a.first_name && a.last_name ? " " : "") + (a.last_name || "") || "(no name)"}
                      </p>
                      {a.checked_in_at ? (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-600/20 text-emerald-300 font-body font-semibold">
                          <CheckCircle2 className="h-3 w-3" /> Checked in
                        </span>
                      ) : null}
                      {/* Desktop: badges inline. Mobile: hidden, revealed in
                          the expanded section below. */}
                      {a.passport_id ? (
                        <span className="hidden sm:inline-flex">
                          <InlineItinerary
                            attendeeId={a.id}
                            itinerary={itineraryCache[a.passport_id]}
                          />
                        </span>
                      ) : null}
                    </div>
                    {a.email ? (
                      <p className="text-xs font-body text-[#6E7787] mt-0.5 truncate">{a.email}</p>
                    ) : null}
                  </div>
                </div>

                {/* Desktop-only inline actions */}
                <div className="hidden sm:flex items-center gap-2 flex-wrap shrink-0">
                  <select
                    value={a.passport_id || ""}
                    onChange={(e) => setPassport(a, e.target.value)}
                    disabled={savingPassportFor === a.id || !passports?.length}
                    className="h-8 rounded bg-[#0A0C10] border border-white/10 text-white text-xs font-body px-2 disabled:opacity-50 focus:outline-none focus:border-white/30"
                    data-testid={`attendee-passport-select-${a.id}`}
                  >
                    <option value="">— No passport —</option>
                    {(passports || []).map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => openEdit(a)}
                    className="h-8 w-8 rounded flex items-center justify-center text-[#A1A9B8] hover:text-white hover:bg-white/5"
                    title="Edit"
                    data-testid={`attendee-edit-${a.id}`}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => setDeleteTarget(a)}
                    className="h-8 w-8 rounded flex items-center justify-center text-red-400 hover:bg-red-500/10"
                    title="Remove"
                    data-testid={`attendee-delete-${a.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>

                {/* Mobile-only expand chevron */}
                <button
                  type="button"
                  onClick={() => toggleExpanded(a.id)}
                  className="sm:hidden h-8 w-8 rounded flex items-center justify-center text-[#A1A9B8] hover:text-white hover:bg-white/5 shrink-0"
                  title={isExpanded ? "Collapse" : "Expand"}
                  data-testid={`attendee-expand-${a.id}`}
                  aria-expanded={isExpanded}
                >
                  <ChevronDown className={`h-4 w-4 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                </button>
              </div>

              {/* Mobile expanded section: passport, itinerary, edit/remove —
                  stacked vertically so nothing escapes the viewport. */}
              {isExpanded ? (
                <div className="sm:hidden mt-3 pt-3 border-t border-white/5 space-y-3" data-testid={`attendee-expanded-${a.id}`}>
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider font-body font-semibold text-[#6E7787] mb-1">Passport</label>
                    <select
                      value={a.passport_id || ""}
                      onChange={(e) => setPassport(a, e.target.value)}
                      disabled={savingPassportFor === a.id || !passports?.length}
                      className="w-full h-9 rounded bg-[#0A0C10] border border-white/10 text-white text-sm font-body px-2 disabled:opacity-50 focus:outline-none focus:border-white/30"
                      data-testid={`attendee-passport-select-mobile-${a.id}`}
                    >
                      <option value="">— No passport —</option>
                      {(passports || []).map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                  {a.passport_id ? (
                    <div>
                      <label className="block text-[10px] uppercase tracking-wider font-body font-semibold text-[#6E7787] mb-1">Itinerary</label>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <InlineItinerary
                          attendeeId={a.id}
                          itinerary={itineraryCache[a.passport_id]}
                        />
                      </div>
                    </div>
                  ) : null}
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => openEdit(a)}
                      className="flex-1 gap-1.5 border-white/15 text-white bg-transparent hover:bg-white/5"
                      data-testid={`attendee-edit-mobile-${a.id}`}
                    >
                      <Pencil className="h-3.5 w-3.5" /> Edit
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setDeleteTarget(a)}
                      className="flex-1 gap-1.5 border-red-400/30 text-red-400 bg-transparent hover:bg-red-500/10"
                      data-testid={`attendee-delete-mobile-${a.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Remove
                    </Button>
                  </div>
                </div>
              ) : null}
            </li>
            );
          })}
        </ul>
      )}

      {/* Add modal */}
      <Dialog open={addOpen} onOpenChange={(v) => { if (!adding) setAddOpen(v); }}>
        <DialogContent className="bg-[#11141A] border-white/10 text-white" data-testid="attendee-add-modal">
          <DialogHeader>
            <DialogTitle className="font-body font-bold text-lg text-white">Add Attendee</DialogTitle>
            <DialogDescription className="text-[#6E7787] font-body text-sm">
              Add a single guest. They&apos;ll be able to check in by typing their name at the event QR.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[#A1A9B8] font-body text-xs">First name</Label>
                <Input value={addFirst} onChange={(e) => setAddFirst(e.target.value)} className="bg-[#0A0C10] border-white/10 text-white mt-1" data-testid="attendee-add-first" />
              </div>
              <div>
                <Label className="text-[#A1A9B8] font-body text-xs">Last name</Label>
                <Input value={addLast} onChange={(e) => setAddLast(e.target.value)} className="bg-[#0A0C10] border-white/10 text-white mt-1" data-testid="attendee-add-last" />
              </div>
            </div>
            <div>
              <Label className="text-[#A1A9B8] font-body text-xs">Email (optional)</Label>
              <Input type="email" value={addEmail} onChange={(e) => setAddEmail(e.target.value)} className="bg-[#0A0C10] border-white/10 text-white mt-1" data-testid="attendee-add-email" />
            </div>
            <div>
              <Label className="text-[#A1A9B8] font-body text-xs">Passport (optional)</Label>
              <select
                value={addPassportId}
                onChange={(e) => setAddPassportId(e.target.value)}
                disabled={!passports?.length}
                className="w-full h-10 rounded-md bg-[#0A0C10] border border-white/10 text-white text-sm font-body px-3 mt-1 disabled:opacity-50 focus:outline-none focus:border-white/30"
                data-testid="attendee-add-passport"
              >
                <option value="">— No passport —</option>
                {(passports || []).map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              {!passports?.length ? (
                <p className="text-[10px] font-body text-[#6E7787] mt-1">No passports defined for this event yet.</p>
              ) : null}
            </div>
          </div>
          <DialogFooter className="gap-3 sm:gap-4 sm:space-x-0">
            <Button variant="outline" onClick={() => setAddOpen(false)} disabled={adding} className="border-white/20 text-white bg-transparent hover:bg-white/5">Cancel</Button>
            <Button onClick={submitAdd} disabled={adding} style={{ background: "var(--primary-color)", color: "var(--admin-button-fg, #fff)" }} data-testid="attendee-add-submit">
              {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit modal */}
      <Dialog open={!!editTarget} onOpenChange={(v) => { if (!saving && !v) setEditTarget(null); }}>
        <DialogContent className="bg-[#11141A] border-white/10 text-white" data-testid="attendee-edit-modal">
          <DialogHeader>
            <DialogTitle className="font-body font-bold text-lg text-white">Edit Attendee</DialogTitle>
            <DialogDescription className="text-[#6E7787] font-body text-sm">Update this guest&apos;s details.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[#A1A9B8] font-body text-xs">First name</Label>
                <Input value={editFirst} onChange={(e) => setEditFirst(e.target.value)} className="bg-[#0A0C10] border-white/10 text-white mt-1" data-testid="attendee-edit-first" />
              </div>
              <div>
                <Label className="text-[#A1A9B8] font-body text-xs">Last name</Label>
                <Input value={editLast} onChange={(e) => setEditLast(e.target.value)} className="bg-[#0A0C10] border-white/10 text-white mt-1" data-testid="attendee-edit-last" />
              </div>
            </div>
            <div>
              <Label className="text-[#A1A9B8] font-body text-xs">Email</Label>
              <Input type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} className="bg-[#0A0C10] border-white/10 text-white mt-1" data-testid="attendee-edit-email" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)} disabled={saving} className="border-white/20 text-white bg-transparent hover:bg-white/5">Cancel</Button>
            <Button onClick={submitEdit} disabled={saving} style={{ background: "var(--primary-color)", color: "var(--admin-button-fg, #fff)" }} data-testid="attendee-edit-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import modal */}
      <Dialog open={importOpen} onOpenChange={(v) => { if (!importing) { setImportOpen(v); if (!v) setImportPreview([]); } }}>
        <DialogContent className="bg-[#11141A] border-white/10 text-white max-w-2xl" data-testid="attendee-import-modal">
          <DialogHeader>
            <DialogTitle className="font-body font-bold text-lg text-white">Import Attendees from CSV</DialogTitle>
            <DialogDescription className="text-[#6E7787] font-body text-sm">
              CSV must include <code className="text-white">first_name</code>, <code className="text-white">last_name</code>, and optionally <code className="text-white">email</code> columns. Duplicates (by name) are skipped automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              onChange={onFile}
              className="block w-full text-sm text-[#A1A9B8] file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:text-sm file:font-body file:font-semibold file:bg-white/10 file:text-white hover:file:bg-white/20"
              data-testid="attendee-import-file"
            />
            {importPreview.length > 0 ? (
              <div className="rounded border border-white/10 max-h-64 overflow-auto" data-testid="attendee-import-preview">
                <table className="w-full text-xs font-body text-white">
                  <thead className="text-[10px] uppercase text-[#6E7787] bg-white/5">
                    <tr>
                      <th className="text-left px-3 py-2">First name</th>
                      <th className="text-left px-3 py-2">Last name</th>
                      <th className="text-left px-3 py-2">Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importPreview.slice(0, 50).map((r, i) => (
                      <tr key={i} className="border-t border-white/5">
                        <td className="px-3 py-2">{r.first_name}</td>
                        <td className="px-3 py-2">{r.last_name}</td>
                        <td className="px-3 py-2 text-[#A1A9B8]">{r.email}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {importPreview.length > 50 ? (
                  <p className="text-[10px] text-[#6E7787] px-3 py-2 border-t border-white/5">
                    Showing first 50 of {importPreview.length} rows.
                  </p>
                ) : null}
              </div>
            ) : (
              <p className="text-xs font-body text-[#6E7787]">Choose a file to preview rows before importing.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)} disabled={importing} className="border-white/20 text-white bg-transparent hover:bg-white/5">
              <X className="h-4 w-4 mr-1" /> Cancel
            </Button>
            <Button
              onClick={submitImport}
              disabled={importing || !importPreview.length}
              style={{ background: "var(--primary-color)", color: "var(--admin-button-fg, #fff)" }}
              data-testid="attendee-import-submit"
            >
              {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : `Import ${importPreview.length || ""}`.trim()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}
        onConfirm={confirmDelete}
        title="Remove attendee?"
        description={deleteTarget ? `Remove ${deleteTarget.first_name || ""} ${deleteTarget.last_name || ""} from this event? Their passport assignment and check-in stamp will also be removed.` : ""}
        confirmLabel="Remove"
      />

      <AddAttendeesFromCohortModal
        open={fromCohortOpen}
        onOpenChange={setFromCohortOpen}
        eventId={eventId}
        onAdded={() => fetchAttendees()}
      />
    </div>
  );
}

function fmt12h(hhmm) {
  if (!hhmm || typeof hhmm !== "string") return "";
  const m = /^(\d{1,2}):(\d{2})/.exec(hhmm);
  if (!m) return hhmm;
  let h = parseInt(m[1], 10);
  const mm = m[2];
  const period = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${mm} ${period}`;
}

function InlineItinerary({ attendeeId, itinerary }) {
  if (itinerary === undefined) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/5 text-[#6E7787] font-body"
        data-testid={`attendee-itinerary-loading-${attendeeId}`}
      >
        <Loader2 className="h-3 w-3 animate-spin" /> Loading itinerary…
      </span>
    );
  }
  if (!itinerary.length) return null;
  return (
    <span
      className="inline-flex flex-wrap items-center gap-1.5"
      data-testid={`attendee-itinerary-${attendeeId}`}
    >
      {itinerary.map((s, i) => {
        const range = s.start_time
          ? `${fmt12h(s.start_time)}${s.end_time ? ` – ${fmt12h(s.end_time)}` : ""}`
          : "";
        const where = s.room || s.session_location || "";
        return (
          <span
            key={s.session_id || i}
            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white border border-black/10 text-[13px] font-body text-black max-w-full overflow-hidden"
            data-testid={`itinerary-badge-${attendeeId}-${i}`}
          >
            <span className="text-black tabular-nums shrink-0">{i + 1}.</span>
            <span className="font-semibold text-black truncate min-w-0 max-w-[140px] sm:max-w-[160px]" title={s.name || ""}>{s.name || "(unnamed)"}</span>
            {range ? (
              <span className="inline-flex items-center gap-0.5 text-black tabular-nums shrink-0 whitespace-nowrap">
                <Clock className="h-3 w-3 opacity-70" /> {range}
              </span>
            ) : null}
            {where ? (
              <span className="inline-flex items-center gap-0.5 text-black shrink-0 whitespace-nowrap">
                <MapPin className="h-3 w-3" /> {where}
              </span>
            ) : null}
          </span>
        );
      })}
    </span>
  );
}
